import { useEffect, useState, type ChangeEvent } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@zentrack/ui";

export interface ClientEditorDialogProps {
  initialName?: string;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => Promise<void>;
  open: boolean;
  title: string;
}

const MIN_NAME_LENGTH = 4;

export function ClientEditorDialog({
  initialName = "",
  onOpenChange,
  onSave,
  open,
  title,
}: ClientEditorDialogProps) {
  const [name, setName] = useState(initialName);
  const [hasEditedName, setHasEditedName] = useState(false);
  const trimmedName = name.trim();
  const nameValidationMessage =
    trimmedName.length > 0 && trimmedName.length < MIN_NAME_LENGTH
      ? `Client name must be at least ${MIN_NAME_LENGTH} characters.`
      : null;
  const canSave = trimmedName.length >= MIN_NAME_LENGTH;

  useEffect(() => {
    if (open) {
      setName(initialName);
      setHasEditedName(false);
    }
  }, [initialName, open]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSave) {
              return;
            }

            void onSave(trimmedName);
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>Use a short client name.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Label htmlFor="client-name">Client name</Label>
            <Input
              autoFocus
              id="client-name"
              onBlur={() => setHasEditedName(true)}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (!hasEditedName) {
                  setHasEditedName(true);
                }
                setName(event.target.value);
              }}
              value={name}
            />
            {hasEditedName && nameValidationMessage ? (
              <p className="text-sm text-destructive">{nameValidationMessage}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              Cancel
            </Button>
            <Button disabled={!canSave} type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
